<!-- Copyright (c) 2023 MOCA Systems, Inc. All rights reserved. -->
<form [formGroup]="formGroup" (ngSubmit)="onNext()">
	<div mat-dialog-content class="dialog-content">
		<!-- Destination Plan Select -->
		<mat-extended-select
			class="full-width"
			formControlName="planId"
			placeholder="Destination Plan"
			automationId="planId"
			[includeFilter]="false"
			[option$]="importTicketsState.planList"
			[labelPath]="['view', 'name']"
			[valuePath]="['$id']"
		>
		</mat-extended-select>
		<span></span>

		<!-- Data Source -->
		<mat-extended-select
			formControlName="dataSource"
			[placeholder]="appStrings.IMPORT_TICKET_DATA_SOURCE_LABEL"
			automationId="dataSource"
			[includeFilter]="false"
			[option$]="dataSourceList$"
			[labelPath]="['name']"
			[sortOptions]="false"
			[required]="true"
			[valuePath]="[]"
		>
		</mat-extended-select>
		<p>
			{{ appStrings.IMPORT_TICKET_DATA_SOURCE_TEXT }}
			<button
				class="link-btn"
				mat-button
				color="primary"
				type="button"
				(click)="onLearnMoreClick(dataSourceLearnMore)"
			>
				{{ appStrings.UICONTROL_TEXT_LEARNMORE }}
			</button>
		</p>

		<!-- Import Behavior Dialog -->
		<ng-template #dataSourceLearnMore>
			<dialog-titlebar
				[showIconButton]="false"
				[title]="appStrings.IMPORT_TICKET_DATA_SOURCE_LEAN_MORE_TITLE"
			></dialog-titlebar>
			<div mat-dialog-content>
				<p>{{ appStrings.IMPORT_TICKET_DATA_SOURCE_LEAN_MORE_DESCRIPTION }}</p>
				<div class="link-data-source-container">
					<button
						class="link-btn"
						mat-button
						automationId="learnMoreDataSourceLink"
						color="primary"
						type="button"
						(click)="onEditDataSource()"
					>
						{{ appStrings.IMPORT_TICKET_DATA_SOURCE_EDIT_BTN_TEXT }}
					</button>
				</div>
			</div>
		</ng-template>
		<p *ngIf="selectedPlanWarningText$ | async as selectedPlanWarningText" class="selected-plan-warning">
			{{ selectedPlanWarningText }}
		</p>

		<!-- BoardScan Image Upload -->
		<ng-container *ngIf="isBoardScanImportSelected$ | async; else excelUpload">
			<ng-container *ngIf="hasSelectedBoardScanFiles$ | async; else noBoardScanFilesSelected">
				<mat-form-field>
					<tp-file-upload
						#fileInput
						formControlName="bsFiles"
						[outputFormat]="UploadOutputFormat.File"
						allowMultiple="true"
					>
						<ng-template fileUploadCustomSlot>
							<div (click)="$event.stopPropagation()" class="bs-files-field-container">
								<mat-icon>image</mat-icon>
								<span>{{ appStrings.IMPORT_TICKET_BS_ATTACHED_FILES_COUNT(boardScanFileItems.length) }}</span>
								<mat-icon
									fontIcon="close"
									color="warn"
									class="bs-files-remove-icon"
									(click)="$event.stopPropagation(); removeBoardScanItems()"
								></mat-icon>
								<button
									class="link-btn"
									mat-button
									color="primary"
									type="button"
									(click)="$event.stopPropagation(); openBoardScanImageCollectionDialog()"
								>
									{{ appStrings.IMPORT_TICKET_BS_UPLOAD_MORE_BUTTON }}
								</button>
							</div>
						</ng-template>
					</tp-file-upload>
				</mat-form-field>
			</ng-container>
			<ng-template #noBoardScanFilesSelected>
				<div class="bs-upload-photos-btn-container">
					<button
						mat-stroked-button
						color="primary"
						type="button"
						class="bs-upload-photos-btn"
						[ngClass]="{
							'error': formGroup.controls.bsFiles.invalid && formGroup.controls.bsFiles.dirty,
						}"
						(click)="formGroup.controls.bsFiles.markAsDirty(); openBoardScanImageCollectionDialog()"
					>
						{{ appStrings.IMPORT_TICKET_BS_UPLOAD_PHOTOS_BUTTON }}*
					</button>
				</div>
			</ng-template>
			<!-- BEGIN PLACEHOLDER REPLACE AS PART OF TP-7075 -->
			<!-- Consolidate text and link -->
			<div>
				<p>
					The system will process your board photos and generate a preview list of your tickets.
					<button
						class="link-btn"
						mat-button
						color="primary"
						type="button"
						(click)="onLearnMoreClick(bsLeanMore)"
					>
						See Instructions
					</button>
				</p>
			</div>
			<ng-template #bsLeanMore>
				<dialog-titlebar [showIconButton]="false" [title]="'Hi'"></dialog-titlebar>
			</ng-template>
			<!-- END PLACEHOLDER REPLACE AS PART OF TP-7075 -->
		</ng-container>

		<!-- Import File Upload -->
		<ng-template #excelUpload>
			<mat-form-field class="file-upload">
				<tp-file-upload
					fileUploadSingle
					formControlName="importFile"
					[buttonText]="appStrings.IMPORT_TICKETS_BUTTON_TEXT"
					[placeholder]="appStrings.IMPORT_TICKETS_FILE_PLACEHOLDER"
					[outputFormat]="UploadOutputFormat.File"
					[acceptedFileTypes]="ticketImportSupportedFormats"
				>
				</tp-file-upload>
				<mat-hint *ngIf="(ticketImportFileUploadStatus$ | async) === ticketImportFileUploadStatus.Success">
					{{ appStrings.IMPORT_TICKETS_FILE_UPLOAD_SUCCESS }}
				</mat-hint>
				<mat-error *ngIf="importFileFormControl.hasError('dataFormatIncorrect')">
					{{ appStrings.IMPORT_TICKETS_TEMPLATE_INVALID_FORMAT }}
				</mat-error>
				<mat-error *ngIf="importFileFormControl.hasError('required')">
					{{ appStrings.FIELD_IS_REQUIRED }}
				</mat-error>
			</mat-form-field>
			<div>
				<p>{{ appStrings.IMPORT_TICKET_FILE_UPLOAD_TEXT }}</p>
				<p>
					{{ appStrings.IMPORT_TICKET_FILE_TO_CREATE_TEXT }}
					<button
						class="download-sample-button"
						mat-stroked-button
						type="button"
						color="primary"
						(click)="onDownloadTicketImportBlankSample()"
					>
						{{ appStrings.DOWNLOAD_BLANK_SAMPLE_BUTTON | uppercase }}
					</button>
				</p>
			</div>
		</ng-template>

		<!-- Import Scope -->
		<mat-extended-select
			formControlName="importScope"
			[placeholder]="appStrings.IMPORT_TICKET_SCOPE_PLACEHOLDER"
			[includeFilter]="false"
			[multiSelect]="true"
			[option$]="importScopeList$"
			[valuePath]="[]"
		>
		</mat-extended-select>
		<div>
			<p>{{ appStrings.IMPORT_TICKET_SCOPE_TEXT }}</p>
		</div>

		<!-- Default Role -->
		<mat-extended-select
			formControlName="defaultRole"
			placeholder="Default Role"
			automationId="defaultRole"
			[includeFilter]="false"
			[option$]="importTicketsState.roleList"
			[labelPath]="['view', 'roleName']"
			[valuePath]="['$id']"
			[iconBackgroundColorPath]="['view', 'color']"
		>
		</mat-extended-select>
		<p>{{ appStrings.IMPORT_TICKET_DEFAULT_ROLE_TEXT_R1 }}</p>

		<!-- Import Type -->
		<mat-extended-select
			formControlName="importType"
			placeholder="Import Type"
			automationId="importType"
			[includeFilter]="false"
			[option$]="importTypeList$"
			[valuePath]="[]"
			[sortOptions]="false"
		>
		</mat-extended-select>
		<div>
			<p>
				{{ appStrings.IMPORT_TICKET_DEFAULT_ROLE_TEXT_R2 }}
				<button
					class="link-btn"
					mat-button
					color="primary"
					type="button"
					(click)="onLearnMoreClick(importBehaviorLearnMore)"
				>
					{{ appStrings.UICONTROL_TEXT_LEARNMORE }}
				</button>
				<!-- Import Behavior Dialog -->
				<ng-template #importBehaviorLearnMore>
					<dialog-titlebar
						[showIconButton]="false"
						[title]="appStrings.IMPORT_BEHAVIOR_TITLE"
					></dialog-titlebar>
					<div mat-dialog-content>
						<p>{{ appStrings.IMPORT_BEHAVIOR_DESCRIPTION }}</p>
						<ul>
							<li>
								<strong>{{ appStrings.IMPORT_BEHAVIOR_OPTION_CREATE_ONLY_NEW_TICKETS_TITLE }}</strong>
								{{ appStrings.IMPORT_BEHAVIOR_OPTION_CREATE_ONLY_NEW_TICKETS_DESCRIPTION }}
							</li>
							<li>
								<strong>{{ appStrings.IMPORT_BEHAVIOR_OPTION_CREATE_WITH_DUPLICATES_TITLE }}</strong>
								{{ appStrings.IMPORT_BEHAVIOR_OPTION_CREATE_WITH_DUPLICATES_DESCRIPTION }}
							</li>
							<li>
								<strong>{{ appStrings.IMPORT_BEHAVIOR_OPTION_UPDATE_ONLY_TITLE }}</strong>
								{{ appStrings.IMPORT_BEHAVIOR_OPTION_UPDATE_ONLY_DESCRIPTION }}
							</li>
							<li>
								<strong>{{ appStrings.IMPORT_BEHAVIOR_OPTION_CREATE_AND_UPDATE_TITLE }}</strong>
								{{ appStrings.IMPORT_BEHAVIOR_OPTION_CREATE_AND_UPDATE_DESCRIPTION }}
							</li>
						</ul>
					</div>
				</ng-template>
			</p>
		</div>

		<!-- Swimlane & WBS -->
		<div>
			<mat-extended-select
				formControlName="swimlanePlacement"
				[placeholder]="appStrings.IMPORT_TICKET_SWIMLANE_PLACEMENT_FIELD_LABEL"
				automationId="swimlanePlacement"
				[includeFilter]="false"
				[option$]="swimlanePlacementList$"
				[sortOptions]="false"
				[valuePath]="[]"
				[required]="false"
			>
			</mat-extended-select>
			<mat-extended-select
				formControlName="wbsLevel"
				[placeholder]="appStrings.IMPORT_TICKET_SWIMLANE_WBS_LEVEL_LABEL"
				automationId="wbsSwimlaneLevel"
				[includeFilter]="false"
				[option$]="wbsSwimlaneLevelList$"
				[sortOptions]="false"
			>
			</mat-extended-select>
		</div>
		<div>
			<p>
				{{ appStrings.IMPORT_TICKET_SWIMLANE_PLACEMENT_TEXT }}
				<button
					class="link-btn"
					mat-button
					color="primary"
					type="button"
					(click)="onLearnMoreClick(swimlanePlacementLearnMore)"
				>
					{{ appStrings.UICONTROL_TEXT_LEARNMORE }}
				</button>
				<!-- Swimlane Placement Dialog -->
				<ng-template #swimlanePlacementLearnMore>
					<dialog-titlebar
						[showIconButton]="false"
						[title]="appStrings.SWIMLANE_PLACEMENT_TITLE"
					></dialog-titlebar>
					<div mat-dialog-content>
						<p>{{ appStrings.SWIMLANE_PLACEMENT_DESCRIPTION }}</p>
						<p>{{ appStrings.SWIMLANE_PLACEMENT_FACTORS_TITLE }}</p>
						<ul>
							<li>
								<strong>{{ appStrings.SWIMLANE_PLACEMENT_FACTOR_ROLE_TITLE }}</strong>
								{{ appStrings.SWIMLANE_PLACEMENT_FACTOR_ROLE_DESCRIPTION }}
							</li>
							<li>
								<strong>{{ appStrings.SWIMLANE_PLACEMENT_FACTOR_LOCATION_TITLE }}</strong>
								{{ appStrings.SWIMLANE_PLACEMENT_FACTOR_LOCATION_DESCRIPTION }}
							</li>
							<li>
								<strong>{{ appStrings.SWIMLANE_PLACEMENT_FACTOR_WBS_TITLE }}</strong>
								{{ appStrings.SWIMLANE_PLACEMENT_FACTOR_WBS_DESCRIPTION }}
							</li>
						</ul>
					</div>
				</ng-template>
			</p>
		</div>
	</div>

	<mat-divider class="padded-divider"></mat-divider>

	<!-- Footer -->
	<button-footer
		mat-dialog-actions
		(buttonSaveOrClose)="onNext()"
		[saveOrCloseButtonText]="appStrings.UICONTROL_TEXT_NEXT | uppercase"
	>
		<button leftButton type="button" mat-button mat-dialog-close color="primary" automationId="cancel">
			{{ appStrings.UICONTROL_TEXT_CANCEL.toUpperCase() }}
		</button>
	</button-footer>
</form>
